import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { DashboardShell } from "@/components/layout/DashboardShell";
import { carrierNav } from "@/pages/carrier/nav";
import { api, getToken, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, FileText, Trash2, Eye, AlertTriangle, Clock } from "lucide-react";

const CATEGORIES = ["Rate Confirmation", "BOL", "POD", "Invoice", "W-9", "Insurance", "Authority Document", "Truck Document", "Trailer Document", "Driver Document", "Other"];
const EXPIRING_CATS = ["Insurance", "Authority Document", "Truck Document", "Trailer Document", "Driver Document"];

function expiryBadge(dateStr) {
  if (!dateStr) return null;
  const days = Math.round((new Date(dateStr) - new Date()) / 86400000);
  if (days < 0) return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-xd-red bg-xd-red/10 border border-xd-red/30 rounded px-1.5 py-0.5"><AlertTriangle size={11} /> Expired</span>;
  if (days <= 30) return <span className="inline-flex items-center gap-1 text-[11px] font-bold text-xd-yellow bg-xd-yellow/10 border border-xd-yellow/30 rounded px-1.5 py-0.5"><Clock size={11} /> {days}d left</span>;
  return <span className="text-[11px] text-xd-dim">Exp {dateStr}</span>;
}

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [cat, setCat] = useState("W-9");
  const [expiration, setExpiration] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const fetch = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { fetch(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", cat);
    if (expiration) fd.append("expiration_date", expiration);
    try { await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); setExpiration(""); fetch(); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id) => { try { await api.delete(`/documents/${id}`); toast.success("Removed"); fetch(); } catch { toast.error("Error"); } };
  const view = (id) => window.open(`${API}/documents/${id}/download?auth=${getToken()}`, "_blank");

  const grouped = CATEGORIES.map((c) => ({ cat: c, items: docs.filter((d) => d.category === c) })).filter((g) => g.items.length);
  const showExpiry = EXPIRING_CATS.includes(cat);

  return (
    <DashboardShell nav={carrierNav} title="Document Vault">
      <div className="bg-xd-paper border border-xd-border rounded-xl p-6 mb-6">
        <h2 className="font-heading text-lg font-bold mb-4">Upload a document</h2>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div>
            <Label className="text-xd-muted text-xs">Category</Label>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="doc-category" className="mt-1 sm:w-56 bg-xd-surface border-xd-border"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {showExpiry && (
            <div>
              <Label className="text-xd-muted text-xs">Expiration date <span className="text-xd-dim">(for alerts)</span></Label>
              <Input data-testid="doc-expiration" type="date" value={expiration} onChange={(e) => setExpiration(e.target.value)} className="mt-1 sm:w-44 bg-xd-surface border-xd-border" />
            </div>
          )}
          <input ref={fileRef} type="file" onChange={onUpload} className="hidden" data-testid="doc-file-input" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.csv" />
          <Button data-testid="doc-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()} className="bg-xd-orange hover:bg-xd-orangeH text-white">
            {uploading ? <Loader2 className="animate-spin" size={16} /> : <><Upload size={16} className="mr-2" /> Choose File</>}
          </Button>
        </div>
      </div>

      {docs.length === 0 ? (
        <div className="py-16 text-center text-xd-muted border border-dashed border-xd-border rounded-xl">No documents yet. Upload W-9, COI and Authority docs to speed up verification.</div>
      ) : (
        <div className="space-y-6">
          {grouped.map((g) => (
            <div key={g.cat}>
              <h3 className="text-xs uppercase tracking-wide font-bold text-xd-dim mb-2">{g.cat}</h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {g.items.map((d) => (
                  <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center gap-3 bg-xd-paper border border-xd-border rounded-lg p-4">
                    <FileText size={22} className="text-xd-orange shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{d.original_filename}</p>
                      <p className="text-xs text-xd-dim flex items-center gap-2">{(d.size / 1024).toFixed(0)} KB {d.expiration_date && expiryBadge(d.expiration_date)}</p>
                    </div>
                    <button data-testid={`doc-view-${d.id}`} onClick={() => view(d.id)} className="text-xd-muted hover:text-xd-blue"><Eye size={16} /></button>
                    <button data-testid={`doc-del-${d.id}`} onClick={() => del(d.id)} className="text-xd-muted hover:text-xd-red"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
